/**
 * ResubscribeCard.tsx — "start a new plan" on the patient's own portal.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: end-of-term re-subscribe.
 *
 * WHY IT EXISTS. A plan that runs its full term ends, and one-month plans
 * deliberately do not auto-renew — the patient must actively choose again. Until
 * now there was no way for them to. This is that way, for the patients who can
 * do it themselves.
 *
 * A NEW TERM IS A NEW CONTRACT, so the commitment gate is asked AGAIN. The tick
 * they gave at first checkout covered the term they bought; it does not carry
 * forward. That is why this reuses `checkout/CommitmentTerms` verbatim rather
 * than paraphrasing it — one wording, reviewed once, shown wherever a patient
 * commits to a term.
 *
 * THREE THINGS ARE SAID BEFORE THE ACTION, NOT AFTER IT: the term, the money,
 * and that a clinician must approve before any of it is taken. The last one is
 * not reassurance copy — it is what actually happens, and a patient who thinks
 * they have just bought medicine will complain when a clinician says no.
 *
 * WHAT WE DO NOT OFFER HERE: a different treatment (that is a new clinical
 * decision, not a repeat) and a different term (we are priced for one term only
 * — see the note by `TERM_NOTE`). Both stay with the back office in v1.
 *
 * THE PRICE SHOWN IS THE SERVER'S PRICE FOR THE NEW TERM, never the finished
 * plan's. This lane creates and confirms in one request, so the tick below is
 * the moment the patient is bound — see the guard in `ResubscribeCard`.
 *
 * WHY IT DOES NOT ASK THE PLAN SECTION TO REFETCH afterwards. That reload swaps
 * the section for its skeleton, which would UNMOUNT this card and destroy the
 * one thing the patient needs to see — the confirmation that their new plan is
 * with a clinician. The server's answer IS the newer truth here; the next visit
 * to the section reads it fresh.
 *
 * RENDER-NOTHING IS THE DEFAULT. No eligibility answer from the server, or a
 * "no", and this component is absent — never a disabled control, never a reason.
 * A patient does not need to be told why the service will not sell to them; that
 * conversation belongs to a human.
 *
 * R28: category-level copy only — the treatment name here comes from the plan
 * read, never from this tree.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { resubscribeSelf } from '../api/orders';
import type { CurrentPlan, PlanMonths, ResubscribeConfirmResult } from '../api/types';
import { CommitmentTerms } from '../checkout/CommitmentTerms';
import { formatPence } from '../lib/format';
import { FAILURE_REASON_COPY_PATIENT, monthsLabel } from '../resubscribe/resubscribeCopy';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Alert, Button, Card, Help } from '../ui/kit';
import { SectionTitle } from '../ui/sections';

/**
 * WHY THE TERM IS FIXED TO THE ONE THEY ALREADY HAVE. Offering "3 months instead
 * of 1" would mean quoting a figure for a term we have not been priced for. A
 * different term is a phone call to the team, who can re-price it properly.
 */
const TERM_NOTE = 'Want a different length of plan? Contact us and we will sort it out with you.';

/**
 * The GUARD. Split from the form below on purpose: three tests have to pass
 * before any of this exists, and doing them in a wrapper means the form can take
 * plain non-optional props rather than carrying optionals it must keep
 * re-checking.
 *
 * THE THIRD TEST IS THE PRICE, AND IT IS A MONEY-SAFETY GATE, NOT A NICETY.
 * The self-serve lane creates AND confirms in one request, so the tick on this
 * card is the patient's binding agreement to a term — and the server re-resolves
 * the price at initiate rather than copying the old one. The plan's own
 * `monthlyPence` is the price of the term that has FINISHED; if the price has
 * moved since, quoting it here means the patient commits at one number and is
 * held at another. Under the Consumer Contracts Regulations 2013 the total price
 * must be given before the consumer is bound, so this renders NOTHING until the
 * server tells us today's figure on the eligibility block.
 *
 * That is the same "render nothing rather than guess" rule the rest of this file
 * follows, and it fails in the safe direction: while orders is still on the
 * revision that omits the price, a patient who wants another term goes through
 * the admin-initiated lane, whose emailed confirmation page IS server-priced.
 */
export function ResubscribeCard({ plan }: { plan: CurrentPlan }) {
  const offer = plan.resubscribe;
  // No answer, a "no", nothing to send, or no price we were actually given for
  // the NEW term: say nothing at all.
  if (!plan.subscriptionId || !offer?.eligible) return null;
  if (offer.monthlyPence === undefined || offer.months === undefined) return null;
  return (
    <ResubscribeForm
      subscriptionId={plan.subscriptionId}
      months={offer.months}
      monthlyPence={offer.monthlyPence}
    />
  );
}

function ResubscribeForm({
  subscriptionId,
  months,
  monthlyPence,
}: {
  subscriptionId: string;
  /** TODAY'S term and price, from the server — never the finished plan's. */
  months: PlanMonths;
  monthlyPence: number;
}) {
  const [accepted, setAccepted] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResubscribeConfirmResult | null>(null);

  const totalPence = months * monthlyPence;

  function start() {
    setBusy(true);
    setError(null);
    // `months` is deliberately omitted — the server defaults it to the prior
    // term, which is the term the eligibility block priced for us above. Sending
    // it explicitly would let this screen and the write disagree about which
    // term was quoted.
    resubscribeSelf({ priorSubscriptionId: subscriptionId })
      .then((confirmed) => {
        setBusy(false);
        setConfirmOpen(false);
        setResult(confirmed);
      })
      .catch(() => {
        setBusy(false);
        setConfirmOpen(false);
        setError('Sorry — we could not start your new plan just now. Please try again.');
      });
  }

  if (result) return <ResubscribeOutcome result={result} />;

  return (
    <Card>
      <SectionTitle>Start a new plan</SectionTitle>
      <Help>
        Your plan has finished. You can start a new one at the price below — nothing is charged until a clinician
        has reviewed and approved your treatment.
      </Help>

      {error ? (
        <Alert tone="danger" role="alert" style={{ marginBottom: 'var(--gps-s-3)' }}>
          {error}
        </Alert>
      ) : null}

      {/* The same gate, the same wording, as the first checkout — a new term is
          a new contract, so the tick is required again. */}
      <CommitmentTerms
        months={months}
        monthlyPence={monthlyPence}
        totalPence={totalPence}
        accepted={accepted}
        onChange={setAccepted}
      />

      <div style={{ marginTop: 'var(--gps-s-4)' }}>
        <Button type="button" variant="cta" disabled={!accepted || busy} onClick={() => setConfirmOpen(true)}>
          Start a new plan
        </Button>
      </div>

      <Help style={{ marginTop: 'var(--gps-s-3)', marginBottom: 0 }}>{TERM_NOTE}</Help>

      <ConfirmDialog
        open={confirmOpen}
        title="Start a new plan?"
        confirmLabel="Yes, start it"
        busy={busy}
        body={
          <>
            <p style={{ margin: '0 0 8px' }}>
              This starts a new {monthsLabel(months)} plan at {formatPence(monthlyPence)} a month —{' '}
              {formatPence(totalPence)} in total.
            </p>
            <p style={{ margin: 0 }}>
              A clinician has to review and approve your treatment before anything is charged. If they don’t, you
              pay nothing.
            </p>
          </>
        }
        onConfirm={start}
        onCancel={() => {
          if (!busy) setConfirmOpen(false);
        }}
      />
    </Card>
  );
}

/**
 * What happened after the patient pressed the button.
 *
 * `payment_action_required` is a stall, not a failure, and the copy has to say
 * so: nothing was taken, and the patient finishes with a card. All three
 * underlying reasons (their bank wants to authenticate them, the saved card was
 * declined, no saved card) end in the same action, which is why they share one
 * screen — but the reason is still stated, because "something went wrong" tells
 * a patient nothing they can act on.
 */
function ResubscribeOutcome({ result }: { result: ResubscribeConfirmResult }) {
  const needsPayment = result.state === 'payment_action_required';
  return (
    <Card>
      <SectionTitle>Your new plan</SectionTitle>
      {/* role="status" so a screen-reader user hears the outcome — the button
          that produced it has gone. */}
      <div role="status">
        {needsPayment ? (
          <>
            <Alert tone="warning" style={{ marginBottom: 'var(--gps-s-3)' }}>
              {result.failureReason
                ? FAILURE_REASON_COPY_PATIENT[result.failureReason]
                : 'We could not take payment automatically.'}{' '}
              You can finish with any card — nothing has been charged yet.
            </Alert>
            {result.paymentLinkUrl ? (
              <a
                href={result.paymentLinkUrl}
                style={{ fontWeight: 600, color: 'var(--gps-link)' }}
                rel="noreferrer"
              >
                Finish setting up your plan
              </a>
            ) : (
              <Help style={{ marginBottom: 0 }}>
                We have emailed you a link to finish setting up your plan.
              </Help>
            )}
          </>
        ) : (
          <>
            <Alert tone="success" style={{ marginBottom: 'var(--gps-s-3)' }}>
              Thank you — your new plan is with a clinician for review.
            </Alert>
            <Help style={{ marginBottom: 0 }}>
              We will let you know as soon as they have looked at it. Nothing is charged until they approve it.
            </Help>
          </>
        )}
      </div>
      <div style={{ marginTop: 'var(--gps-s-3)' }}>
        <Link to="/patients" style={{ fontWeight: 600, color: 'var(--gps-link)' }}>
          Back to your dashboard
        </Link>
      </div>
    </Card>
  );
}
